import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabaseClient'
import jsPDF from 'jspdf'
import './Orcamentos.css'

/**
 * Tela de Orçamentos: cadastro e consulta por empresa do usuário.
 * Dados da empresa preenchidos automaticamente; cliente e itens pelo usuário.
 * PDF por orçamento; acesso via permissão de menu (exceto administrador).
 */
const Orcamentos = ({ onBack, userEmpresa, userId }) => {
  const empresaId = userEmpresa?.id

  // Dados do formulário (novo orçamento)
  const [clienteNome, setClienteNome] = useState('')
  const [clienteTelefone, setClienteTelefone] = useState('')
  const [clienteEndereco, setClienteEndereco] = useState('')
  const [itemQuantidade, setItemQuantidade] = useState('1')
  const [itemDescricao, setItemDescricao] = useState('')
  const [itemValor, setItemValor] = useState('')
  const [itensTemp, setItensTemp] = useState([]) // { id, quantidade, item, valor, total }

  const [orcamentos, setOrcamentos] = useState([])
  const [orcamentosItens, setOrcamentosItens] = useState({}) // orcamento_id -> itens[]
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState({ text: '', type: '' })

  const totalTemp = itensTemp.reduce((acc, i) => acc + (Number(i.total) || 0), 0)

  // Parse do valor digitado (BR: 1.500,00 ou 1500,00) para número
  const parseValorInput = (str) => {
    if (!str || typeof str !== 'string') return 0
    const s = str.trim().replace(/\./g, '').replace(',', '.')
    return parseFloat(s) || 0
  }

  const handleValorChange = (e) => {
    const raw = e.target.value
    if (raw === '') {
      setItemValor('')
      return
    }
    const onlyDigitsAndComma = raw.replace(/[^\d,]/g, '').replace(/,/g, (m, i, s) => s.indexOf(',') === i ? ',' : '')
    if (onlyDigitsAndComma === '') {
      setItemValor('')
      return
    }
    const [intPart, decPart] = onlyDigitsAndComma.split(',')
    const intFormatted = (intPart || '0').replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    const dec = (decPart || '').substring(0, 2)
    setItemValor(dec ? `${intFormatted},${dec}` : intFormatted)
  }

  const loadOrcamentos = useCallback(async () => {
    if (!empresaId) return
    try {
      const { data, error } = await supabase
        .from('orcamentos')
        .select('*')
        .eq('empresa_id', empresaId)
        .order('created_at', { ascending: false })
      if (error) throw error
      setOrcamentos(data || [])
      const ids = (data || []).map(o => o.id)
      if (ids.length === 0) {
        setOrcamentosItens({})
        return
      }
      const { data: itens, error: errItens } = await supabase
        .from('orcamento_itens')
        .select('*')
        .in('orcamento_id', ids)
        .order('ordem')
      if (errItens) throw errItens
      const byOrc = {}
      ;(itens || []).forEach(i => {
        if (!byOrc[i.orcamento_id]) byOrc[i.orcamento_id] = []
        byOrc[i.orcamento_id].push(i)
      })
      setOrcamentosItens(byOrc)
    } catch (e) {
      console.error(e)
      setMsg({ text: e.message || 'Erro ao carregar orçamentos.', type: 'error' })
    } finally {
      setLoading(false)
    }
  }, [empresaId])

  useEffect(() => {
    loadOrcamentos()
  }, [loadOrcamentos])

  const addItem = () => {
    const qtd = parseFloat(itemQuantidade.replace(',', '.')) || 0
    const valor = parseValorInput(itemValor)
    const item = (itemDescricao || '').trim()
    if (!item) {
      setMsg({ text: 'Informe a descrição do item.', type: 'error' })
      return
    }
    const total = qtd * valor
    setItensTemp(prev => [...prev, {
      id: crypto.randomUUID?.() || Date.now(),
      quantidade: qtd,
      item,
      valor,
      total
    }])
    setItemQuantidade('1')
    setItemDescricao('')
    setItemValor('')
    setMsg({ text: '', type: '' })
  }

  const removeItemTemp = (id) => {
    setItensTemp(prev => prev.filter(i => i.id !== id))
  }

  const handleSalvar = async (e) => {
    e.preventDefault()
    const nome = (clienteNome || '').trim()
    if (!nome) {
      setMsg({ text: 'Nome do cliente é obrigatório.', type: 'error' })
      return
    }
    if (!empresaId) {
      setMsg({ text: 'Empresa não identificada. Faça login novamente.', type: 'error' })
      return
    }
    if (itensTemp.length === 0) {
      setMsg({ text: 'Adicione pelo menos um item ao orçamento.', type: 'error' })
      return
    }
    setSaving(true)
    setMsg({ text: '', type: '' })
    try {
      const totalGeral = itensTemp.reduce((acc, i) => acc + (Number(i.total) || 0), 0)
      const { data: orc, error: errOrc } = await supabase
        .from('orcamentos')
        .insert([{
          empresa_id: empresaId,
          empresa_nome: userEmpresa?.empresa || userEmpresa?.nome,
          empresa_razao_social: userEmpresa?.razao_social || '',
          empresa_cnpj: userEmpresa?.cnpj || '',
          empresa_endereco: userEmpresa?.endereco || '',
          empresa_telefone: userEmpresa?.contato || '',
          cliente_nome: nome,
          cliente_telefone: (clienteTelefone || '').trim() || null,
          cliente_endereco: (clienteEndereco || '').trim() || null,
          total_geral: totalGeral
        }])
        .select('id')
        .single()
      if (errOrc) throw errOrc
      const orcamentoId = orc.id
      const itensInsert = itensTemp.map((row, idx) => ({
        orcamento_id: orcamentoId,
        quantidade: row.quantidade,
        item: row.item,
        valor_unitario: row.valor,
        total: row.total,
        ordem: idx
      }))
      const { error: errItens } = await supabase.from('orcamento_itens').insert(itensInsert)
      if (errItens) throw errItens
      setMsg({ text: 'Orçamento salvo com sucesso.', type: 'success' })
      setClienteNome('')
      setClienteTelefone('')
      setClienteEndereco('')
      setItensTemp([])
      loadOrcamentos()
      setTimeout(() => setMsg({ text: '', type: '' }), 4000)
    } catch (e) {
      setMsg({ text: e.message || 'Erro ao salvar orçamento.', type: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const gerarPDF = (orcamento) => {
    const itens = orcamentosItens[orcamento.id] || []
    const doc = new jsPDF('p', 'mm', 'a4')
    const margin = 20
    const pageWidth = doc.internal.pageSize.getWidth()
    let y = margin
    const font = 10
    doc.setFontSize(14)
    doc.text('ORÇAMENTO', margin, y)
    y += 10
    doc.setFontSize(font)
    doc.text(`Cliente: ${orcamento.cliente_nome || '-'}`, margin, y)
    y += 6
    if (orcamento.cliente_telefone) {
      doc.text(`Telefone: ${orcamento.cliente_telefone}`, margin, y)
      y += 6
    }
    if (orcamento.cliente_endereco) {
      doc.text(`Endereço: ${orcamento.cliente_endereco}`, margin, y)
      y += 6
    }
    y += 6
    doc.setFontSize(11)
    doc.text('Dados da empresa', margin, y)
    y += 6
    doc.setFontSize(font)
    doc.text(`${orcamento.empresa_nome || '-'}`, margin, y)
    y += 5
    if (orcamento.empresa_razao_social) { doc.text(`Razão Social: ${orcamento.empresa_razao_social}`, margin, y); y += 5 }
    if (orcamento.empresa_cnpj) { doc.text(`CNPJ: ${orcamento.empresa_cnpj}`, margin, y); y += 5 }
    if (orcamento.empresa_endereco) { doc.text(`Endereço: ${orcamento.empresa_endereco}`, margin, y); y += 5 }
    if (orcamento.empresa_telefone) { doc.text(`Telefone: ${orcamento.empresa_telefone}`, margin, y); y += 5 }
    y += 8
    doc.setFontSize(11)
    doc.text('Itens', margin, y)
    y += 6
    const colQtd = margin
    const colItem = margin + 22
    const colValor = pageWidth - margin - 50
    const colTotal = pageWidth - margin - 28
    doc.setFontSize(font)
    doc.text('Qtd', colQtd, y)
    doc.text('Item', colItem, y)
    doc.text('Valor', colValor, y)
    doc.text('Total', colTotal, y)
    y += 6
    itens.forEach(linha => {
      if (y > 270) { doc.addPage(); y = margin }
      doc.text(String(linha.quantidade), colQtd, y)
      doc.text((linha.item || '').substring(0, 50), colItem, y)
      doc.text(`R$ ${Number(linha.valor_unitario).toFixed(2).replace('.', ',')}`, colValor, y)
      doc.text(`R$ ${Number(linha.total).toFixed(2).replace('.', ',')}`, colTotal, y)
      y += 6
    })
    y += 6
    doc.setFont('helvetica', 'bold')
    doc.text(`Total Geral: R$ ${Number(orcamento.total_geral).toFixed(2).replace('.', ',')}`, margin, y)
    doc.setFont('helvetica', 'normal')
    doc.save(`orcamento_${(orcamento.cliente_nome || 'cliente').replace(/\W/g, '_')}.pdf`)
  }

  const formatMoney = (n) => `R$ ${Number(n).toFixed(2).replace('.', ',')}`
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('pt-BR') : '-'

  return (
    <div className="orcamentos-container">
      <header className="orcamentos-header">
        <button type="button" className="orcamentos-btn-back" onClick={onBack} aria-label="Voltar">
          ← Voltar
        </button>
        <h1 className="orcamentos-title">Orçamentos</h1>
      </header>

      {msg.text && (
        <div className={`orcamentos-msg orcamentos-msg-${msg.type}`} role="alert">
          {msg.text}
        </div>
      )}

      {/* Formulário novo orçamento */}
      <section className="orcamentos-section">
        <h2 className="orcamentos-section-title">Novo orçamento</h2>
        <form onSubmit={handleSalvar} className="orcamentos-form">
          <div className="orcamentos-form-grid">
            <label className="orcamentos-label-input">
              Nome do cliente <span className="orcamentos-required">*</span>
              <input
                type="text"
                value={clienteNome}
                onChange={e => setClienteNome(e.target.value)}
                placeholder="Nome completo"
                className="orcamentos-input"
                required
              />
            </label>
            <label className="orcamentos-label-input">
              Telefone
              <input
                type="text"
                value={clienteTelefone}
                onChange={e => setClienteTelefone(e.target.value)}
                placeholder="(00) 00000-0000"
                className="orcamentos-input"
              />
            </label>
            <label className="orcamentos-label-input orcamentos-label-full">
              Endereço
              <input
                type="text"
                value={clienteEndereco}
                onChange={e => setClienteEndereco(e.target.value)}
                placeholder="Endereço do cliente"
                className="orcamentos-input"
              />
            </label>
          </div>

          <h3 className="orcamentos-subtitle">Itens do orçamento</h3>
          <div className="orcamentos-itens-form">
            <div className="orcamentos-itens-row">
              <label className="orcamentos-label-input orcamentos-qtd">
                Quantidade
                <input
                  type="text"
                  value={itemQuantidade}
                  onChange={e => setItemQuantidade(e.target.value)}
                  placeholder="1"
                  className="orcamentos-input"
                  inputMode="decimal"
                />
              </label>
              <label className="orcamentos-label-input orcamentos-item-desc">
                Item
                <input
                  type="text"
                  value={itemDescricao}
                  onChange={e => setItemDescricao(e.target.value)}
                  placeholder="Descrição do item"
                  className="orcamentos-input"
                />
              </label>
              <label className="orcamentos-label-input orcamentos-valor">
                Valor
                <input
                  type="text"
                  value={itemValor}
                  onChange={handleValorChange}
                  placeholder="0,00"
                  className="orcamentos-input"
                  inputMode="decimal"
                />
              </label>
              <div className="orcamentos-add-wrap">
                <button type="button" className="orcamentos-btn-add" onClick={addItem}>
                  Adicionar
                </button>
              </div>
            </div>
          </div>

          {itensTemp.length > 0 && (
            <div className="orcamentos-table-wrap">
              <table className="orcamentos-table">
                <thead>
                  <tr>
                    <th>Quantidade</th>
                    <th>Item</th>
                    <th>Valor</th>
                    <th>Total</th>
                    <th className="orcamentos-th-action" aria-label="Remover" />
                  </tr>
                </thead>
                <tbody>
                  {itensTemp.map(row => (
                    <tr key={row.id}>
                      <td>{Number(row.quantidade)}</td>
                      <td>{row.item}</td>
                      <td>{formatMoney(row.valor)}</td>
                      <td>{formatMoney(row.total)}</td>
                      <td>
                        <button type="button" className="orcamentos-btn-remove" onClick={() => removeItemTemp(row.id)} aria-label="Remover item">
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="3" className="orcamentos-tfoot-label">Total</td>
                    <td className="orcamentos-tfoot-total">{formatMoney(totalTemp)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          <div className="orcamentos-form-actions">
            <button type="submit" className="orcamentos-btn orcamentos-btn-primary" disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar orçamento'}
            </button>
          </div>
        </form>
      </section>

      {/* Lista de orçamentos cadastrados */}
      <section className="orcamentos-section">
        <h2 className="orcamentos-section-title">Orçamentos cadastrados</h2>
        {loading ? (
          <p className="orcamentos-loading">Carregando...</p>
        ) : orcamentos.length === 0 ? (
          <p className="orcamentos-empty">Nenhum orçamento cadastrado.</p>
        ) : (
          <div className="orcamentos-list">
            {orcamentos.map(orc => (
              <div key={orc.id} className="orcamentos-card">
                <div className="orcamentos-card-header">
                  <strong>{orc.cliente_nome}</strong>
                  <span className="orcamentos-card-date">{formatDate(orc.created_at)}</span>
                </div>
                <div className="orcamentos-card-body">
                  {orc.cliente_telefone && <p>Tel: {orc.cliente_telefone}</p>}
                  <p className="orcamentos-card-total">{formatMoney(orc.total_geral)}</p>
                </div>
                <div className="orcamentos-card-actions">
                  <button type="button" className="orcamentos-btn orcamentos-btn-pdf" onClick={() => gerarPDF(orc)}>
                    Gerar PDF
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

export default Orcamentos
